import { InterventionReport } from '@inplay/models';
import { ReportDetailsConfig } from './reportDetails.types';
import {
  formatMinutesToHoursAndMinutes,
  formatDateWithTime,
  getDisplayName,
} from '../../utils/helper';
import { getInterventionReportRevieweStatusDotColor } from '../../pages/interventionReports/interventionReportsHelper';

export const createInterventionReportConfig = (
  interventionReport: InterventionReport,
): ReportDetailsConfig => ({
  titleKey: 'interventionReports.report',
  reportRefNo: interventionReport.reportRefNo,
  sections: [
    {
      id: 'generalInfo',
      titleKey: 'reportDetails.generalInfo',
      type: 'general-info',
      data: {
        fields: [
          {
            labelKey: 'reportDetails.reportNo',
            value: interventionReport.reportRefNo || '-',
            column: 1,
          },
          {
            labelKey: 'reportDetails.venue',
            value: interventionReport.venueBranch?.name || '-',
            column: 1,
          },
          {
            labelKey: 'createInterventionReport.interventionConductedBy',
            value: getDisplayName(interventionReport.conductedBy),
            column: 1,
          },
          {
            labelKey: 'interventionReports.interventionConductedOn',
            value: formatDateWithTime(interventionReport.conductedAt) || '-',
            column: 1,
          },
          {
            labelKey: 'interventionReports.harmInstances',
            value: interventionReport.harmInstances ?? '-',
            column: 2,
          },
          {
            labelKey: 'interventionReports.linkedHarmReports',
            value: interventionReport.harmReport?.join(', ') || '-',
            column: 2,
          },
          {
            labelKey: 'reportDetails.interventionStatus',
            dotColor:
              getInterventionReportRevieweStatusDotColor(
                interventionReport.reviewStatus,
              ) || '-',
            value: interventionReport.reviewStatus || '-',
            column: 2,
            isStatus: true,
          },
        ],
      },
    },
    {
      id: 'playerPhotos',
      titleKey: 'reportDetails.playerPhotos',
      type: 'player-photos',
      data: {
        faceImage: interventionReport.playerProfile?.faceImage,
        bodyImage: interventionReport.playerProfile?.bodyImage,
      },
    },
    {
      id: 'playerDetails',
      titleKey: 'reportDetails.playerDetails',
      type: 'player-details',
      data: {
        leftColumn: [
          {
            labelKey: 'reportDetails.playerNo',
            value: interventionReport.playerProfile.id.slice(0, 5),
          },
          {
            labelKey: 'interventionReports.nameNickname',
            value: interventionReport.playerDetails?.name || '-',
          },
          {
            labelKey: 'reportDetails.description',
            value: interventionReport.playerDetails?.description || '-',
          },
          {
            labelKey: 'reportDetails.gender',
            value: interventionReport.playerDetails?.gender || '-',
          },
          {
            labelKey: 'reportDetails.age',
            value: interventionReport.playerDetails?.age || '-',
          },
          {
            labelKey: 'reportDetails.ethnicity',
            value: interventionReport.playerDetails?.ethnicity || '-',
          },
        ],
        rightColumn: [
          {
            labelKey: 'reportDetails.currentUninterruptedTime',
            value: formatMinutesToHoursAndMinutes(
              interventionReport.playerDetails?.gambling?.uninterruptedTime,
            ),
          },
          {
            labelKey: 'reportDetails.gamblingAtmUse',
            value:
              interventionReport.playerDetails?.gambling?.gamblingATMUse || '-',
          },
          {
            labelKey: 'interventionReports.currentConsecutiveSweep',
            value:
              interventionReport.playerDetails?.gambling
                ?.consecutiveSweepsCount || '-',
          },
          {
            labelKey: 'reportDetails.totalInterventions',
            value: `${interventionReport.playerDetails?.gambling?.interventionsOffered || 0} / ${interventionReport.playerDetails?.gambling?.interventionsRequested || 0}`,
          },
          {
            labelKey: 'reportDetails.totalTimeInGambling',
            value: formatMinutesToHoursAndMinutes(
              interventionReport.playerDetails?.gambling?.totalTimeInGameArea,
            ),
          },
          {
            labelKey: 'reportDetails.totalTimeOutside',
            value: formatMinutesToHoursAndMinutes(
              interventionReport.playerDetails?.gambling
                ?.totalTimeOutsideGameArea,
            ),
          },
        ],
      },
    },
    {
      id: 'signsOfGamblingHarm',
      titleKey: 'reportDetails.signsOfGamblingHarm',
      type: 'harm-signs',
      data: {
        lengthOfPlay:
          interventionReport.signsOfGamblingHarm?.lengthOfPlay || [],
        money: interventionReport.signsOfGamblingHarm?.money || [],
        behaviour: interventionReport.signsOfGamblingHarm?.behaviour || [],
        other: interventionReport.signsOfGamblingHarm?.other || [],
      },
    },
    {
      id: 'actionTaken',
      titleKey: 'interventionReports.actionTaken',
      type: 'action-list',
      data: {
        items: interventionReport.interventionAction || [],
      },
    },
    {
      id: 'notesObservations',
      titleKey: 'reportDetails.notesObservations',
      type: 'notes',
      data: {
        content: interventionReport.createdBy?.notes || '-',
      },
    },
    {
      id: 'createdBy',
      titleKey: 'reportDetails.createdBy',
      type: 'signature',
      data: {
        signature: interventionReport.createdBy,
        isAutoGenerated: !interventionReport.createdBy,
        autoGeneratedDate: interventionReport.createdAt,
      },
    },
  ],
  hasReviewSection: Boolean(interventionReport.reviewedBy),
  reviewData: interventionReport.reviewedBy
    ? {
        staffCompliance: interventionReport.staffComplied,
        followUpActions: interventionReport.followUpAction,
        comments: interventionReport.reviewedBy.notes,
        reviewedBy: interventionReport.reviewedBy,
      }
    : undefined,
});
